import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  PhoneIncoming,
  Users,
  XCircle,
  Zap,
  UserCircle,
  ArrowRight,
  Bot,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useTeamGroups, useAvailableTeamAgents } from '@/api/teamGroups'
import type { NumeroAvecOccupant } from '@/api/aiAgents'
import {
  useApplyAnswerMode,
  useAvailableAIPhoneNumbers,
  type AnswerMode,
  type AnswerModeResult,
} from '@/api/aiAgents'

interface Props {
  isOpen: boolean
  onClose: () => void
  companyId: number | undefined
  agentId: number
  agentName: string
  /** Ouvert depuis la page d'un numéro : ce numéro est imposé, le choix disparaît. */
  lockedPhoneNumberId?: number
  /** Équipe qui sonne déjà sur ce numéro (flux existant) : pré-remplie. */
  currentTeamId?: number | 'all' | null
  /** Où l'agent répond déjà (page Agents IA) : on le dit avant de reposer la question. */
  answersIn?: { number?: string | null; flows?: string[] }
  /** Ouvre la page du numéro (bouton « Voir ce numéro »). */
  onOpenNumber?: () => void
}

/**
 * « Quand votre agent doit-il répondre ? » — le client répond à une question
 * métier ; le backend génère l'assignation directe ou le flux IVR
 * (time_check / débordement) sans jamais montrer l'éditeur de flux.
 * Le flux généré reste visible et modifiable dans « Numéros et flux ».
 */
export function AnswerModeModal({
  isOpen,
  onClose,
  companyId,
  agentId,
  agentName,
  lockedPhoneNumberId,
  currentTeamId,
  answersIn,
  onOpenNumber,
}: Props) {
  const { t } = useTranslation()
  const apply = useApplyAnswerMode(companyId)
  const { data: numbersData } = useAvailableAIPhoneNumbers(companyId, agentId)
  const { data: teams = [] } = useTeamGroups()
  const { data: disponibles } = useAvailableTeamAgents(isOpen)
  const personnes = disponibles?.web_agents ?? []
  // Une seule personne dans l'entreprise : c'est elle qui répond, sans question.
  const seul = personnes.length === 1 && teams.length === 0

  const [mode, setMode] = useState<AnswerMode>('always')
  const [phoneNumberId, setPhoneNumberId] = useState<string>('')
  // Cible humaine : 'all' | 'team:<id>' | 'user:<profil>'
  const [qui, setQui] = useState<string>('all')
  // Sonnerie des humains avant l'agent (secondes)
  const [sonnerie, setSonnerie] = useState<number>(30)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AnswerModeResult | null>(null)
  // Nom du flux IVR déjà branché sur le numéro : le backend refuse de le
  // débrancher sans consentement (flow_will_be_replaced) — on affiche une
  // confirmation puis on réessaie avec replaceFlow: true.
  const [replaceFlowName, setReplaceFlowName] = useState<string | null>(null)

  // Tous les numéros, avec ce qui y répond : on choisit en connaissance de
  // cause, et « remplacer » est dit avant de cliquer.
  const numbers = useMemo(() => {
    const decrire = (n: NumeroAvecOccupant): { detail: string; bloque: boolean } => {
      const r = n.answers_today
      if (n.is_current) return { detail: t('manager.aiAgents.answerMode.occupant.current', 'cet agent'), bloque: false }
      if (!r) return { detail: t('manager.aiAgents.answerMode.occupant.free', 'libre'), bloque: false }
      if (r.kind === 'flow') return { detail: t('manager.aiAgents.answerMode.occupant.flow', 'flux « {{name}} », sera remplacé', { name: r.name }), bloque: false }
      if (r.kind === 'agent') return { detail: t('manager.aiAgents.answerMode.occupant.agent', '{{name}} répond, à libérer d\'abord', { name: r.name }), bloque: true }
      if (r.kind === 'power_dialer') return { detail: t('manager.aiAgents.answerMode.occupant.powerDialer', 'power dialer'), bloque: true }
      return { detail: t('manager.aiAgents.answerMode.occupant.team', 'votre équipe'), bloque: false }
    }
    const list: { id: string; label: string; detail: string; bloque: boolean }[] = (numbersData?.all_numbers ?? []).map((n) => {
      const d = decrire(n)
      return { id: String(n.id), label: n.label ? `${n.phone_number} · ${n.label}` : n.phone_number, ...d }
    })
    if (
      numbersData?.company_main_number &&
      !list.some((n) => n.label.startsWith(numbersData.company_main_number ?? ''))
    ) {
      list.unshift({
        id: 'main',
        label: `${numbersData.company_main_number} (${t('manager.aiAgents.answerMode.mainNumber')})`,
        detail: t('manager.aiAgents.answerMode.occupant.team', 'votre équipe'),
        bloque: false,
      })
    }
    return list
  }, [numbersData, t])

  // L'équipe n'est utile que pour les modes avec humains
  const needsTeam = mode !== 'always'

  // La confirmation de remplacement vaut pour LA cible affichée : tout
  // changement de mode ou de numéro l'invalide.
  useEffect(() => {
    setReplaceFlowName(null)
  }, [mode, phoneNumberId, qui])

  useEffect(() => {
    if (isOpen) {
      setMode('always')
      setError(null)
      setResult(null)
      setReplaceFlowName(null)
      // Défaut « tout le monde » (comme les concurrents) ; on garde l'équipe
      // qui sonne déjà sur le numéro s'il y en a une.
      setQui(currentTeamId != null && currentTeamId !== 'all' ? `team:${currentTeamId}` : 'all')
      setSonnerie(30)
      setPhoneNumberId(
        lockedPhoneNumberId != null ? String(lockedPhoneNumberId) : numbers.length === 1 ? numbers[0].id : '',
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Ce qui part au serveur : la personne seule est prise d'office.
  const cible = seul ? `user:${personnes[0].id}` : qui

  const handleApply = async (replaceFlow = false) => {
    setError(null)
    if (!replaceFlow) setReplaceFlowName(null)
    try {
      const res = await apply.mutateAsync({
        agentId,
        mode,
        phoneNumberId: phoneNumberId === 'main' ? 'main' : Number(phoneNumberId),
        teamId: cible.startsWith('team:') ? Number(cible.slice(5)) : undefined,
        userProfileId: cible.startsWith('user:') ? Number(cible.slice(5)) : undefined,
        ringTimeout: sonnerie,
        replaceFlow,
      })
      setReplaceFlowName(null)
      setResult(res)
    } catch (e) {
      const data = e instanceof AxiosError ? e.response?.data : null
      const code = (data?.error as string) || 'generic'
      if (code === 'flow_will_be_replaced') {
        // Pas une erreur : demande de confirmation avant de débrancher le
        // flux IVR existant du numéro.
        setReplaceFlowName((data?.flow_name as string) || '')
      } else if (code === 'number_taken_by_agent') {
        setError(
          t('manager.aiAgents.answerMode.errors.number_taken_by_agent', {
            name: data?.agent_name ?? '',
          }),
        )
      } else if (ERROR_KEYS.includes(code)) {
        setError(t(`manager.aiAgents.answerMode.errors.${code}`))
      } else {
        setError(t('manager.aiAgents.answerMode.errors.generic'))
      }
    }
  }

  /**
   * Aperçu du trajet de l'appel : « Appel entrant → Votre équipe (20 s) →
   * Mira prend un message ». Le client voit la conséquence de ses choix
   * avant de valider, au lieu de deviner ce que « Équipe qui répond »
   * signifie une fois l'appel arrivé.
   */
  function CallPathPreview({
    mode: previewMode,
    agentName: agent,
    teamName,
    needsTeam: withTeam,
  }: {
    mode: AnswerMode
    agentName: string
    teamName: string | null
    needsTeam: boolean
  }) {
    const teamLabel =
      teamName ?? t('manager.aiAgents.answerMode.preview.yourTeam')

    type Step = { text: string; note?: string; ai?: boolean }
    const steps: Step[] = [
      { text: t('manager.aiAgents.answerMode.preview.incoming') },
    ]

    if (previewMode === 'always') {
      steps.push({
        text: t('manager.aiAgents.answerMode.preview.agentAnswers', { name: agent }),
        ai: true,
      })
    } else if (previewMode === 'after_hours') {
      steps.push({
        text: teamLabel,
        note: t('manager.aiAgents.answerMode.preview.whenOpen'),
      })
      steps.push({
        text: t('manager.aiAgents.answerMode.preview.agentTakesMessage', { name: agent }),
        note: t('manager.aiAgents.answerMode.preview.whenClosedOrNoAnswer'),
        ai: true,
      })
    } else {
      steps.push({
        text: teamLabel,
        note: t('manager.aiAgents.answerMode.preview.ringFor', 'sonne {{s}} s', { s: sonnerie }),
      })
      steps.push({
        text: t('manager.aiAgents.answerMode.preview.agentTakesMessage', { name: agent }),
        note: t('manager.aiAgents.answerMode.preview.ifNobodyPicksUp'),
        ai: true,
      })
    }

    // Une étape = une pastille ronde, un titre, une note. Flèche entre les deux.
    const icones = [PhoneIncoming, withTeam ? UserCircle : Bot, Bot]
    return (
      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
        <div className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
          {t('manager.aiAgents.answerMode.preview.title')}
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-3">
          {steps.map((step, i) => {
            const Icone = icones[Math.min(i, icones.length - 1)]
            return (
              <span key={i} className="flex items-center gap-3">
                {i > 0 && <ArrowRight className="h-4 w-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />}
                <span
                  className={cn(
                    'h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0',
                    step.ai ? 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300'
                      : i === 0 ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'
                      : 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
                  )}
                >
                  <Icone className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900 dark:text-white">{step.text}</span>
                  {step.note && <span className="block text-xs text-slate-500 dark:text-slate-400">{step.note}</span>}
                </span>
              </span>
            )
          })}
        </div>
      </div>
    )
  }

  const MODES: { id: AnswerMode; icon: typeof Zap; needsTeam: boolean }[] = [
    { id: 'always', icon: Zap, needsTeam: false },
    { id: 'after_hours', icon: Clock, needsTeam: true },
    { id: 'overflow', icon: Users, needsTeam: true },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneIncoming className="h-5 w-5 text-blue-600" />
            {t('manager.aiAgents.answerMode.title', { name: agentName })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('manager.aiAgents.answerMode.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Résultat */}
        {result ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 p-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-800 dark:text-slate-200">
                <p className="font-medium">
                  {t(`manager.aiAgents.answerMode.success.${result.mode}`, {
                    number: result.phone_number,
                  })}
                </p>
                {result.flow_name && (
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                    {t('manager.aiAgents.answerMode.flowCreated', {
                      name: result.flow_name,
                    })}
                  </p>
                )}
              </div>
            </div>
            <Button type="button" onClick={onClose} className="w-full">
              {t('common.close')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Les 3 réponses possibles */}
            <div className="space-y-2">
              {MODES.map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMode(id)}
                  className={cn(
                    'w-full flex items-center gap-4 rounded-xl border p-3.5 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
                    mode === id
                      ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-950/40'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
                  )}
                >
                  <span className="h-11 w-11 rounded-full bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-300 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold text-slate-900 dark:text-white">
                      {t(`manager.aiAgents.answerMode.modes.${id}.label`)}
                    </span>
                    <span className="block text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                      {t(`manager.aiAgents.answerMode.modes.${id}.description`)}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      mode === id ? 'border-blue-600' : 'border-slate-300 dark:border-slate-600',
                    )}
                    aria-hidden="true"
                  >
                    {mode === id && <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />}
                  </span>
                </button>
              ))}
            </div>

            {/* Aperçu du parcours d'appel.
                Les libellés de champs (« Équipe qui répond ») nomment une
                donnée sans dire ce qui se passera. Cette bande raconte le
                trajet réel de l'appel, avec les valeurs choisies : le client
                lit une phrase, pas un formulaire. */}
            <CallPathPreview
              mode={mode}
              agentName={agentName}
              teamName={
                cible === 'all'
                  ? t('manager.aiAgents.answerMode.teamAll')
                  : cible.startsWith('team:')
                    ? teams.find((team) => String(team.id) === cible.slice(5))?.name ?? null
                    : personnes.find((p) => String(p.id) === cible.slice(5))?.name ?? null
              }
              needsTeam={needsTeam}
            />

            {/* L'agent répond déjà quelque part : on le dit avant de reposer la question */}
            {!!(answersIn?.number || answersIn?.flows?.length) && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-sm text-emerald-800 dark:text-emerald-200">
                <span>
                  {answersIn?.number
                    ? t('manager.aiAgents.answerMode.alreadyOnNumber', 'Répond déjà sur {{number}}.', { number: answersIn.number })
                    : t('manager.aiAgents.answerMode.alreadyInFlow', 'Répond déjà dans le flux « {{flow}} ».', { flow: answersIn?.flows?.[0] })}
                </span>
                {onOpenNumber && (
                  <button type="button" onClick={onOpenNumber} className="font-semibold underline underline-offset-2">
                    {t('manager.aiAgents.answerMode.seeNumber', 'Voir ce numéro')}
                  </button>
                )}
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
            {/* Numéro à brancher (caché quand il est imposé par la page du numéro) */}
            <div className={cn('space-y-2', lockedPhoneNumberId != null && 'hidden')}>
              <Label>{t('manager.aiAgents.answerMode.numberLabel')}</Label>
              {numbers.length ? (
                <Select value={phoneNumberId} onValueChange={setPhoneNumberId}>
                  <SelectTrigger className="w-full min-w-0 max-w-full overflow-hidden [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:overflow-hidden [&>[data-slot=select-value]>span]:truncate">
                    <SelectValue
                      placeholder={t('manager.aiAgents.answerMode.numberPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {numbers.map((n) => (
                      <SelectItem key={n.id} value={n.id} disabled={n.bloque}>
                        <span className="num-tabular">{n.label}</span>
                        <span className="ml-2 text-xs text-slate-500 truncate">· {n.detail}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              {numbers.length ? null : (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  {t('manager.aiAgents.answerMode.noNumberAvailable')}
                </p>
              )}
            </div>

            {/* Qui répond côté humains : toute l'équipe, un groupe, ou une personne.
                Comme dans les transferts : on choisit, on ne devine pas. */}
            {needsTeam && (
              <div className="space-y-2">
                <Label>{t('manager.aiAgents.answerMode.teamLabel')}</Label>
                {seul ? (
                  <div className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
                    <UserCircle className="h-4 w-4 text-slate-400" />
                    {t('manager.aiAgents.answerMode.onlyYou', '{{name}} (vous êtes seul dans l\'entreprise)', { name: personnes[0].name })}
                  </div>
                ) : (
                  <Select value={qui} onValueChange={setQui}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <span className="flex items-center gap-2"><Users className="h-4 w-4" />{t('manager.aiAgents.answerMode.teamAll')}</span>
                      </SelectItem>
                      {teams.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>{t('manager.aiAgents.answerMode.groupTeams', 'Groupes')}</SelectLabel>
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={`team:${team.id}`}>
                              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" />{team.name}</span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      {personnes.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>{t('manager.aiAgents.answerMode.groupPeople', 'Personnes')}</SelectLabel>
                          {personnes.map((p) => (
                            <SelectItem key={p.id} value={`user:${p.id}`}>
                              <span className="flex items-center gap-2"><UserCircle className="h-4 w-4 text-slate-400" />{p.name}</span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <span>{t('manager.aiAgents.answerMode.ringFor', 'Sonne pendant')}</span>
                  <input
                    type="number"
                    min={10}
                    max={90}
                    step={5}
                    value={sonnerie}
                    onChange={(e) => setSonnerie(Math.max(10, Math.min(90, Number(e.target.value) || 30)))}
                    className="w-16 rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1 text-sm num-tabular"
                  />
                  <span>{t('manager.aiAgents.answerMode.secondsLong', 'secondes')}</span>
                </div>
              </div>
            )}
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-400">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Confirmation : un flux IVR est déjà branché sur ce numéro */}
            {replaceFlowName !== null && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-300 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {t('manager.aiAgents.answerMode.replaceFlow.message', {
                      name: replaceFlowName,
                    })}
                  </span>
                </div>
              </div>
            )}

            {/* Un seul pied : Annuler, et une action. Quand un flux doit être
                remplacé, l'action le dit et devient orange. */}
            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11">
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => void handleApply(replaceFlowName !== null)}
                disabled={
                  apply.isPending ||
                  !phoneNumberId ||
                  (needsTeam && !cible)
                }
                className={cn('flex-1 h-11', replaceFlowName !== null ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-blue-600 hover:bg-blue-700')}
              >
                {apply.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {replaceFlowName !== null
                  ? t('manager.aiAgents.answerMode.replaceFlow.confirmAndApply', 'Remplacer le flux et configurer')
                  : t('manager.aiAgents.answerMode.apply')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

const ERROR_KEYS = [
  'phone_number_required',
  'phone_number_not_found',
  'no_main_number',
  'team_required',
  'team_not_found',
  'team_choice_required',
  'outbound_agent',
  'agent_inactive',
]
